'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, Copy, RotateCcw, Trash2, Download, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useForm } from 'react-hook-form';
import Button from '@/components/ui/Button';
import Image from 'next/image';

interface Message {
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    isInitialGreeting?: boolean;
}

type FormData = {
    prompt: string;
};

const SUGGESTED_PROMPTS = [
    "How do I adopt a pet?",
    "What pets are available?",
    "Tell me about the adoption process",
    "How do I care for an exotic pet?",
    "What are the adoption fees?",
    "How do I contact emergency services?"
];

export default function AISupportAgent() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [error, setError] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const { register, handleSubmit, reset, formState, setValue } = useForm<FormData>();
    const [isLoading, setIsLoading] = useState(false);

    // Initialize with greeting
    useEffect(() => {
        if (!isInitialized) {
            setMessages([
                {
                    role: 'assistant',
                    content: "👋 Hi! I'm your **Exotic and Domestic** AI support assistant. I'm here to help you with:\n\n- Pet adoption process\n- Finding your perfect pet match\n- Care guides and resources\n- Emergency services\n- Platform features\n\nHow can I help you today?",
                    timestamp: new Date(),
                    isInitialGreeting: true
                }
            ]);
            setIsInitialized(true);
        }
    }, [isInitialized]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (content: string) => {
        if (!content.trim() || isLoading) return;

        const userMessage: Message = {
            role: 'user',
            content: content.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setShowSuggestions(false);
        setIsLoading(true);
        setError('');

        try {
            const history = messages.map(msg => ({
                role: msg.role,
                content: msg.content,
                isInitialGreeting: msg.isInitialGreeting
            }));
            history.push({ role: 'user', content: content.trim(), isInitialGreeting: undefined });

            const response = await fetch('/api/support-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: content.trim(),
                    history: history
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                const assistantMessage: Message = {
                    role: 'assistant',
                    content: data.response,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else {
                const errorMessage: Message = {
                    role: 'assistant',
                    content: data.error || 'Sorry, I encountered an error. Please try again.',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage: Message = {
                role: 'assistant',
                content: 'Sorry, I encountered a connection error. Please try again.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async ({ prompt }: FormData) => {
        await sendMessage(prompt);
        reset();
    };

    const handleSuggestedPrompt = (prompt: string) => {
        setValue('prompt', prompt);
        sendMessage(prompt);
        reset();
    };

    const copyMessage = (content: string) => {
        navigator.clipboard.writeText(content);
    };

    const regenerateResponse = async () => {
        if (messages.length < 2 || isLoading) return;

        // Find the last user message
        const lastUserMessageIndex = messages.findLastIndex(msg => msg.role === 'user');
        if (lastUserMessageIndex === -1) return;

        const lastUserMessage = messages[lastUserMessageIndex];

        // Remove messages after the last user message
        setMessages(prev => prev.slice(0, lastUserMessageIndex + 1));

        // Resend the last user message
        await sendMessage(lastUserMessage.content);
    };

    const clearChat = () => {
        setMessages([
            {
                role: 'assistant',
                content: "👋 Hi! I'm your **Exotic and Domestic** AI support assistant. I'm here to help you with:\n\n- Pet adoption process\n- Finding your perfect pet match\n- Care guides and resources\n- Emergency services\n- Platform features\n\nHow can I help you today?",
                timestamp: new Date(),
                isInitialGreeting: true
            }
        ]);
        setShowSuggestions(true);
        setError('');
    };

    const exportConversation = () => {
        const conversation = messages
            .map(msg => `[${msg.timestamp.toLocaleString()}] ${msg.role.toUpperCase()}: ${msg.content}`)
            .join('\n\n');

        const blob = new Blob([conversation], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `support-chat-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    return (
        <div className="flex flex-col h-full bg-gradient-to-b from-white to-gray-50 max-w-4xl mx-auto relative rounded-lg shadow-sm border border-gray-200">
            {/* Header with Actions */}
            <div className="flex-none px-4 py-3 border-b border-gray-200 bg-white/80 backdrop-blur-sm flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-brand-primary" />
                    <span className="font-semibold text-gray-800">AI Support Assistant</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={exportConversation}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Export conversation"
                        disabled={messages.length <= 1}
                    >
                        <Download className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                        onClick={clearChat}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Clear chat"
                    >
                        <Trash2 className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
            </div>

            {/* Messages Area - Scrollable */}
            <div className="flex-1 min-h-0 overflow-y-scroll px-4 py-6 space-y-4">
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        {/* Avatar for assistant messages */}
                        {message.role === 'assistant' && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-white border-2 border-brand-primary shadow-sm">
                                <Image
                                    src="/images/logo.png"
                                    alt="AI Assistant"
                                    width={32}
                                    height={32}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}

                        <div className="flex flex-col max-w-[85%] gap-2">
                            <div
                                className={`px-4 py-3 rounded-2xl ${message.role === "user"
                                    ? "bg-brand-primary text-white rounded-tr-sm shadow-md"
                                    : "bg-white text-gray-800 rounded-tl-sm shadow-sm border border-gray-200"
                                    }`}
                            >
                                <div className="prose prose-sm max-w-none prose-p:leading-relaxed prose-p:mb-2 last:prose-p:mb-0">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            a: ({ href, children }) => (
                                                <a
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`underline ${message.role === 'user'
                                                        ? 'text-white hover:text-gray-200'
                                                        : 'text-brand-primary hover:text-brand-secondary'
                                                        }`}
                                                >
                                                    {children}
                                                </a>
                                            ),
                                            p: ({ children }) => <p className="mb-0">{children}</p>
                                        }}
                                    >
                                        {message.content}
                                    </ReactMarkdown>
                                </div>
                            </div>

                            {/* Message Actions */}
                            {message.role === 'assistant' && !message.isInitialGreeting && (
                                <div className="flex items-center gap-2 px-2">
                                    <button
                                        onClick={() => copyMessage(message.content)}
                                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                                        title="Copy message"
                                    >
                                        <Copy className="w-3 h-3" />
                                        Copy
                                    </button>
                                    {index === messages.length - 1 && (
                                        <button
                                            onClick={regenerateResponse}
                                            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                                            title="Regenerate response"
                                            disabled={isLoading}
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                            Regenerate
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* Loading Indicator */}
                {isLoading && (
                    <div className="flex gap-3 justify-start">
                        {/* Avatar for loading state */}
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-white border-2 border-brand-primary shadow-sm">
                            <Image
                                src="/images/logo.png"
                                alt="AI Assistant"
                                width={32}
                                height={32}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <div className="px-4 py-3 rounded-2xl bg-white rounded-tl-sm shadow-sm border border-gray-200">
                            <div className="flex gap-1 h-6 items-center">
                                <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"></span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Suggested Prompts */}
                {showSuggestions && messages.length === 1 && !isLoading && (
                    <div className="space-y-3 pt-4">
                        <p className="text-sm text-gray-600 font-medium">Suggested questions:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {SUGGESTED_PROMPTS.map((prompt, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSuggestedPrompt(prompt)}
                                    className="text-left px-4 py-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 transition-all hover:shadow-md hover:border-brand-primary/30"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {error && <p className="text-red-500 text-sm text-center px-4">{error}</p>}

            {/* Input Area - Fixed Bottom */}
            <div className="flex-none w-full p-4 bg-white border-t border-gray-200">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={onKeyDown}
                    className="flex items-end gap-2 p-2 px-4 border border-gray-300 rounded-3xl bg-gray-50 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-primary/20 focus-within:border-brand-primary transition-all shadow-sm hover:shadow-md"
                >
                    <textarea
                        {...register("prompt", {
                            required: true,
                            validate: (value) => value.trim().length > 0
                        })}
                        className="w-full bg-transparent border-0 focus:ring-0 focus:outline-0 resize-none py-3 max-h-32 min-h-[44px] scrollbar-hide text-sm sm:text-base"
                        autoFocus
                        maxLength={2000}
                        placeholder="Ask me anything about pet adoption..."
                        rows={1}
                        onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = target.scrollHeight + 'px';
                        }}
                    />
                    <Button
                        type="submit"
                        disabled={!formState.isValid || isLoading}
                        className="mb-1 w-9 h-9 rounded-full p-0 flex items-center justify-center shrink-0 shadow-md hover:shadow-lg transition-shadow"
                        size="icon"
                    >
                        <ArrowUp className="w-4 h-4" />
                    </Button>
                </form>
                <p className="text-center text-xs text-gray-400 mt-2">
                    AI can make mistakes. Please verify important information.
                </p>
            </div>
        </div>
    );
}
